/* eslint-disable */
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { Label } from '../../Components/ui/label'
import { Input } from '../../Components/ui/input'
import { Button } from '../../Components/ui/button'
import { post } from '../../http/restservice'
import env from '../../env/env'
import { useModal } from '../../hooks/use-modal-store'
import { toCurrency } from '../../utils/CurrencyUtil'
import { formatIndianNumber } from '../../utils/Util'
import OrderOtpVerification from '../FirstIdeas/OrderOtpVerification'

const Redeem = () => {
  const user =
    JSON.parse(sessionStorage.getItem('actionPlanUser')) &&
    JSON.parse(sessionStorage.getItem('actionPlanUser')).data

  const location = useLocation()
  const navigate = useNavigate()
  const { onOpen } = useModal()
  const [basket] = useState(location?.state?.basket || [])
  const [openOTPPopup, setOpenOTPPopup] = useState(false)
  const [redemptionPercentage, setRedemptionPercentage] = useState(100)
  const [redemptionAmount, setRedemptionAmount] = useState(0)
  const [redemptionAmountDisplay, setRedemptionAmountDisplay] = useState('0')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [redemptionFund, setRedemptionFund] = useState([])

  const basketInfo = Array.isArray(basket) ? basket[0] : basket
  const email = user?.email
  const clientCode =
    location?.state?.clientCode || user?.uid?.split('-')[0]?.toUpperCase()

  const totalCurrentAmount = useMemo(() => {
    if (!Array.isArray(basket)) return 0
    return basket
      .reduce((acc, plan) => acc + (plan.currentNav || 0) * (plan.netUnits || 0), 0)
      .toFixed(3)
  }, [basket])

  useEffect(() => {
    const preciseAmount = Number(totalCurrentAmount) || 0
    setRedemptionAmount(preciseAmount)
    setRedemptionAmountDisplay(String(Math.floor(preciseAmount)))
  }, [totalCurrentAmount])

  const handleRedemptionAmountChange = (e) => {
    setError('')
    const value = e.target.value.replace(/[^0-9]/g, '')
    setRedemptionAmountDisplay(value)
    if (!isNaN(value) && Number(value) >= 0) {
      setRedemptionAmount(Number(value))
    }
  }

  const handleRedemptionPercentage = (btn) => {
    setRedemptionPercentage(btn)
    const amount = Number(totalCurrentAmount) || 0
    const calculatedAmount = (amount * Number(btn)) / 100
    setRedemptionAmount(calculatedAmount)
    setRedemptionAmountDisplay(String(Math.floor(calculatedAmount)))
  }

  const buildRedemptionOrderFund = (fund) => {
    const base = {
      isin: fund.isin,
      folioNo: fund.folioNo,
      schemeCode: fund.schemeCode,
      schemeName: fund.schemeName,
      redemptionType: fund.redemptionType,
      nav: fund.nav,
    }

    if (fund.redemptionType === 'ALL_UNITS') {
      return { ...base, isAllRedeem: 'Y' }
    }

    if (fund.redemptionType === 'UNITS') {
      return {
        ...base,
        isAllRedeem: 'N',
        withdrawableUnits: fund.withdrawableUnits,
      }
    }

    return {
      ...base,
      isAllRedeem: 'N',
      withdrawableAmount: fund.withdrawableAmount,
    }
  }

  const generateOTP = async () => {
    const response = await post(
      `${env.API_URL}mutualfundservice/api/mutualFund/sendTransactionalOtp`,
      {
        phoneNumber: user?.phoneNumber,
        email: email?.includes('fabits') ? '' : email,
      },
    )

    if (response.isError) {
      onOpen('openAdminPopup', {
        title: 'OTP Error',
        message: 'Failed to generate OTP. Please try again later.',
        severity: 'error',
      })
      throw new Error('Failed to generate OTP')
    }

    setOpenOTPPopup(true)
  }

  const showRedemptionBreakdown = (redemptionResponse) => {
    onOpen('openMFRedeemBreakdown', {
      constituents: redemptionResponse,
      amount: Math.round(Number(redemptionAmount)),
      onContinue: generateOTP,
    })
  }

  const placeBasketRedeemOrder = async (redemptionResponse) => {
    const orderData = redemptionResponse.map(buildRedemptionOrderFund)

    const orderPayload = {
      clientCode: clientCode?.toUpperCase(),
      phoneNumber: formatIndianNumber(user?.phoneNumber),
      customerBasketInvestmentId: basketInfo?.customerBasketInvestmentId,
      orderData,
    }

    const basketRedeem = await post(
      `${env.API_URL}mutualfundservice/api/basket/oneTimeRedemptionOrder`,
      orderPayload,
    )

    if (basketRedeem?.isError || basketRedeem?.status === 'FAILURE') {
      onOpen('openAdminPopup', {
        title: 'Redemption Failed!',
        message:
          basketRedeem?.message ||
          'There was an issue with redemption. Please try again later.',
        severity: 'error',
      })
      return null
    }

    if (basketRedeem?.status === 'SUCCESS') {
      const successOrders = basketRedeem.orders?.filter((o) => o?.status === 'SUCCESS')
      if (successOrders?.length === basketRedeem.orders?.length) {
        onOpen('openAdminPopup', {
          title: 'Redemption Initiated!',
          message:
            'Your redemption request has been successfully initiated. Redemption will be completed in 1–5 working days.',
          severity: 'success',
        })
        return basketRedeem
      }
    }

    return basketRedeem
  }

  const handleBasketRedeem = async () => {
    setError('')

    if (Number(redemptionAmount) > Number(totalCurrentAmount)) {
      return setError('Entered amount exceeds available balance')
    }

    if (!redemptionAmount || Number(redemptionAmount) <= 0) {
      return setError('Please enter a valid redemption amount')
    }

    try {
      setIsLoading(true)

      const redemptionPayload = {
        customerBasketInvestmentId: basketInfo?.customerBasketInvestmentId,
        universalBasketId: basketInfo?.universalBasketId || 1,
        withdrawableAmount: Math.round(Number(redemptionAmount)),
        isV2Enabled: true,
      }

      const redemptionResponse = await post(
        `${env.API_URL}mutualfundservice/api/basket/redemptionFunds`,
        redemptionPayload,
      )

      if (redemptionResponse?.isError || !redemptionResponse?.length) {
        onOpen('openAdminPopup', {
          title: 'Redemption Failed!',
          message:
            redemptionResponse?.message ||
            'There was an issue fetching redemption breakdown. Please try again later.',
          severity: 'error',
        })
        return
      }

      setRedemptionFund(redemptionResponse)
      showRedemptionBreakdown(redemptionResponse)
    } catch (err) {
      console.error('Redemption API Error:', err)
      onOpen('openAdminPopup', {
        title: 'Redemption Failed!',
        message: 'A technical error occurred while processing redemption. Please try again later.',
        severity: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div
        className='flex gap-x-1 items-center font-semibold cursor-pointer lg:py-2.5 p-4 lg:px-0'
        onClick={() => navigate(-1)}
      >
        <ChevronLeft className='h-5 w-5' />
        Redemption Breakdown
      </div>

      <div className='max-w-[420px] mx-auto flex flex-col gap-y-6 px-4'>
        <div className='h-[200px] relative flex justify-center items-center shadow-sm border rounded-md'>
          <div className='flex flex-col justify-center items-center text-center w-full'>
            <img
              src={`${env.SVGS}/basket.svg`}
              alt={basketInfo?.customerBasketName}
              className='w-12 h-12'
            />
            <h3 className='font-semibold text-xl'>{basketInfo?.customerBasketName}</h3>

            <div className='flex flex-col items-center justify-center mt-2'>
              <span className='text-sm text-black-400'> Available Balance</span>
              <span className='font-semibold text-primary-500 text-2xl'>
                ₹ {toCurrency(Math.floor(totalCurrentAmount))}
              </span>
            </div>
          </div>
        </div>

        <div className='flex flex-col space-y-6 p-4 rounded-md border'>
          <div className='flex flex-col items-start gap-1.5'>
            <Label htmlFor='amount' className='text-black-400 text-sm'>
              Withdrawal Amount
            </Label>
            <Input
              startAdornment='₹'
              type='amount'
              id='amount'
              placeholder='Amount'
              className='w-full'
              value={redemptionAmountDisplay}
              onChange={handleRedemptionAmountChange}
            />

            {error && <p className='text-center text-sm text-error-500'>{error}</p>}

            <div className='grid grid-cols-3 gap-x-2 mt-2 w-full'>
              {[10, 25, 50].map((btn, index) => (
                <Button
                  key={`${btn}-${index}`}
                  size='simple'
                  className={`p-2 rounded-md shadow-sm ${btn === redemptionPercentage
                    ? 'bg-gray-50 border text-black hover:bg-gray-50'
                    : 'bg-gray-50 text-black-500 hover:bg-gray-50'
                    }`}
                  onClick={() => handleRedemptionPercentage(btn)}
                >
                  {`${btn}%`}
                </Button>
              ))}
            </div>

            <div className='grid grid-cols-3 gap-x-2 w-full'>
              {[75, 100].map((btn, index) => (
                <Button
                  key={`${btn}-${index}`}
                  size='simple'
                  className={`p-2 rounded-md shadow-sm ${btn === 100 ? 'col-span-2' : ''} ${btn === redemptionPercentage
                    ? 'bg-gray-50 border text-black hover:bg-gray-50'
                    : 'bg-gray-50 text-black-500 hover:bg-gray-50'
                    }`}
                  onClick={() => handleRedemptionPercentage(btn)}
                >
                  {btn === 100 ? 'Full' : `${btn}%`}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className='bg-warning-50 dark:bg-warning-950/20 rounded-lg p-4'>
          <div className='flex items-start gap-3'>
            <div className='space-y-1'>
              <h5 className='font-medium text-warning-900 dark:text-warning-100 flex items-center gap-x-1'>
                Important Notice
              </h5>
              <p className='text-sm text-warning-700 dark:text-warning-300'>
                Withdrawn funds will be transferred to your linked bank account within 1-5 business
                days.
              </p>
            </div>
          </div>
        </div>

        <Button
          className='rounded-md bg-primary-500 hover:bg-primary-400 disabled:bg-primary-200'
          loading={isLoading}
          onClick={handleBasketRedeem}
          disabled={
            isLoading || redemptionAmount > Number(totalCurrentAmount) || redemptionAmount <= 0
          }
        >
          View Redemption Breakdown
        </Button>
      </div>

      <OrderOtpVerification
        phoneNumber={user?.phoneNumber}
        openOTPPopup={openOTPPopup}
        setOpenOTPPopup={setOpenOTPPopup}
        email={user?.email}
        handleSipOrOT={() => placeBasketRedeemOrder(redemptionFund)}
      />
    </>
  )
}

export default Redeem
